import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Send, 
  MessageSquare, 
  Users, 
  Wrench, 
  Calendar, 
  Trash2,
  Bot,
  ChevronDown,
  ChevronUp,
  Paperclip,
  FileText,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityPage = () => {
  const { user, isCoach, isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [sharedCritiques, setSharedCritiques] = useState([]);
  const [category, setCategory] = useState('main');
  const [newPost, setNewPost] = useState('');
  const [newPostLink, setNewPostLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [posting, setPosting] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [replyContent, setReplyContent] = useState({});
  const [attachFile, setAttachFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'main', label: 'Main Feed', icon: MessageSquare },
    { id: 'disciplines', label: 'Disciplines', icon: Users },
    { id: 'gear', label: 'Gear & Setup', icon: Wrench },
    { id: 'events', label: 'Events', icon: Calendar },
  ];

  useEffect(() => {
    fetchPosts();
    fetchSharedCritiques();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const fetchPosts = async (retryCount = 0) => {
    try {
      setError(false);
      const response = await axios.get(`${API_URL}/api/community/posts`, {
        params: { category: category === 'main' ? undefined : category },
        timeout: 10000
      });
      setPosts(response.data.posts || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      if (retryCount < 2) {
        setTimeout(() => fetchPosts(retryCount + 1), 1000);
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedCritiques = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community/critiques`);
      setSharedCritiques(response.data.critiques || []);
    } catch (error) {
      console.error('Failed to fetch shared critiques:', error);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    setPosting(true);
    try {
      let attachment_url = null;
      let attachment_name = null;

      if (attachFile) {
        setUploading(true);
        const token = localStorage.getItem('token');
        const urlRes = await axios.post(`${API_URL}/api/community/upload-url`, {
          filename: attachFile.name,
        }, { headers: { Authorization: `Bearer ${token}` } });

        await fetch(urlRes.data.upload_url, {
          method: 'PUT',
          body: attachFile,
        });
        attachment_url = urlRes.data.file_key;
        attachment_name = attachFile.name;
        setUploading(false);
      }

      await axios.post(`${API_URL}/api/community/posts`, {
        content: newPost,
        category: category === 'main' ? 'main' : category,
        link: newPostLink || null,
        attachment_key: attachment_url,
        attachment_name
      });
      setNewPost('');
      setNewPostLink('');
      setAttachFile(null);
      fetchPosts();
    } catch (error) {
      console.error('Failed to create post:', error);
      setUploading(false);
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (postId) => {
    const content = replyContent[postId];
    if (!content?.trim()) return;

    try {
      await axios.post(`${API_URL}/api/community/posts/${postId}/reply`, { content });
      setReplyContent({ ...replyContent, [postId]: '' });
      fetchPosts();
    } catch (error) {
      console.error('Failed to reply:', error);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      await axios.delete(`${API_URL}/api/community/posts/${postId}`);
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleAskAssistant = async (e) => {
    e.preventDefault();
    if (!aiQuestion.trim()) return;

    setAiLoading(true);
    setAiResponse('');
    try {
      const response = await axios.post(`${API_URL}/api/assistant/ask`, {
        question: aiQuestion
      });
      setAiResponse(response.data.response);
    } catch (error) {
      setAiResponse('Sorry, I couldn\'t process your question. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const toggleExpand = (postId) => {
    setExpandedPosts({ ...expandedPosts, [postId]: !expandedPosts[postId] });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            Community
          </h1>
          <p className="text-muted-foreground mt-2">
            Connect with fellow ropers and learn together.
          </p>
        </div>

        {/* AI Training Assistant */}
        <Card className="mb-8 border-secondary/30 bg-secondary/5">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
              <Bot className="w-5 h-5 text-secondary" />
              <span>Training Assistant</span>
              <Badge className="badge-assistant ml-2">AI</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Got a quick question about roping technique? Ask the assistant. For personalized feedback on your runs, submit a critique to the coach.
            </p>
            <form onSubmit={handleAskAssistant} className="space-y-4">
              <Textarea
                placeholder="Ask about roping technique, timing, equipment..."
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                rows={2}
                data-testid="ai-question-input"
              />
              <Button 
                type="submit" 
                disabled={aiLoading || !aiQuestion.trim()}
                className="rounded-full"
                data-testid="ai-submit-btn"
              >
                {aiLoading ? 'Thinking...' : 'Ask Assistant'}
              </Button>
            </form>
            {aiResponse && (
              <div className="ai-response mt-4 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="badge-assistant">Training Assistant</Badge>
                </div>
                <p className="whitespace-pre-wrap">{aiResponse}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Tabs */}
        <Tabs value={category} onValueChange={setCategory} className="mb-6">
          <TabsList className="grid grid-cols-4 h-auto">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <TabsTrigger 
                  key={cat.id} 
                  value={cat.id}
                  className="flex flex-col items-center gap-1 py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  data-testid={`category-${cat.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs">{cat.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* New Post Form */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handlePost} className="space-y-4">
              <Textarea
                placeholder="Share something with the community..."
                value={newPost}
                onChange={(e) => setNewPost(e.target.value.slice(0, 2000))}
                rows={3}
                data-testid="new-post-input"
              />
              <div className="flex items-center gap-4">
                <Input
                  type="url"
                  placeholder="Add a link (optional)"
                  value={newPostLink}
                  onChange={(e) => setNewPostLink(e.target.value)}
                  className="flex-1"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                  capture={false}
                  onChange={(e) => setAttachFile(e.target.files[0] || null)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach document"
                  data-testid="attach-file-btn"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Button 
                  type="submit" 
                  disabled={posting || uploading || !newPost.trim()}
                  className="rounded-full"
                  data-testid="submit-post-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Post'}
                </Button>
              </div>
              {attachFile && (
                <div className="flex items-center gap-2 text-sm bg-muted px-3 py-2 rounded-lg">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="truncate flex-1">{attachFile.name}</span>
                  <button onClick={() => setAttachFile(null)} className="text-muted-foreground hover:text-destructive">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground text-right">{newPost.length}/2000</p>
            </form>
          </CardContent>
        </Card>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : posts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No posts yet in this category</p>
              <p className="text-sm text-muted-foreground mt-1">Be the first to start a conversation</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <Card key={post.id} className="community-post">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">
                          {post.user_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{post.user_name}</span>
                          {post.user_role === 'coach' && <Badge className="badge-coach">Coach</Badge>}
                          {post.user_role === 'admin' && <Badge variant="outline">Admin</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {post.user_region && `${post.user_region} • `}{formatDate(post.created_at)}
                        </p>
                      </div>
                    </div>
                    {(post.user_id === user?.id || isCoach || isAdmin) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(post.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <p className="whitespace-pre-wrap mb-3">{post.content}</p>
                  
                  {post.link && (
                    <a 
                      href={post.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm"
                    >
                      {post.link}
                    </a>
                  )}

                  {post.attachment_url && (
                    /\.(jpg|jpeg|png|gif|webp)/i.test(post.attachment_name || '') ? (
                      <img
                        src={post.attachment_url}
                        alt={post.attachment_name || 'Attached image'}
                        className="mt-2 rounded-lg max-h-96 w-auto object-contain"
                        data-testid={`post-image-${post.id}`}
                      />
                    ) : (
                      <a
                        href={post.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 mt-2 p-2 bg-muted rounded-lg text-sm hover:bg-muted/80 transition-colors w-fit"
                        data-testid={`post-attachment-${post.id}`}
                      >
                        <FileText className="w-4 h-4 text-primary shrink-0" />
                        <span className="truncate">{post.attachment_name || 'Download attachment'}</span>
                      </a>
                    )
                  )}

                  {/* Replies */}
                  {post.replies?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={() => toggleExpand(post.id)}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3"
                      >
                        {expandedPosts[post.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {post.replies.length} {post.replies.length === 1 ? 'reply' : 'replies'}
                      </button>
                      
                      {expandedPosts[post.id] && (
                        <div className="space-y-3 pl-4 border-l-2 border-muted">
                          {post.replies.map(reply => (
                            <div key={reply.id} className="text-sm">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold">{reply.user_name}</span>
                                {reply.user_role === 'coach' && <Badge className="badge-coach text-xs">Coach</Badge>}
                                <span className="text-xs text-muted-foreground">{formatDate(reply.created_at)}</span>
                              </div>
                              <p>{reply.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reply Form */}
                  <div className="mt-4 pt-4 border-t flex gap-2">
                    <Input
                      placeholder="Write a reply..."
                      value={replyContent[post.id] || ''}
                      onChange={(e) => setReplyContent({ ...replyContent, [post.id]: e.target.value })}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReply(post.id)}
                      disabled={!replyContent[post.id]?.trim()}
                      className="rounded-full"
                    >
                      Reply
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Shared Critiques Section */}
        {sharedCritiques.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display font-bold text-xl uppercase tracking-wide mb-6">
              Shared Critiques
            </h2>
            <div className="space-y-4">
              {sharedCritiques.slice(0, 5).map(critique => (
                <Card key={critique.id} className="coach-response">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline">{critique.discipline}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(critique.completed_at)}
                      </span>
                    </div>
                    <p className="text-muted-foreground mb-4">{critique.note}</p>
                    {critique.response && (
                      <div className="bg-primary/5 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="badge-coach">Coach</Badge>
                          <span className="font-semibold">{critique.response.coach_name}</span>
                        </div>
                        <p className="whitespace-pre-wrap">
                          {critique.response.type === 'text' 
                            ? critique.response.content 
                            : `Coach response: ${critique.response.content}`}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPage;
